import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import {
  sendOtpForAcceptingConsent,
  verifyOTP_forConsent,
} from "app/services/orgService/school.service";
import { useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";

const OtpPlanAcceptance = ({ session, sessionInfo, schoolInfo, refetch }) => {
  const [otp, setOtp] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { mutate: sendOtp, isLoading: sendingOtp } = useMutation(
    sendOtpForAcceptingConsent,
    {
      onSuccess: () => {
        setDialogOpen(true);
        toast.success(`OTP sent to ${schoolInfo?.email}`);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to send OTP");
      },
    },
  );

  const { mutate: verifyOtp, isLoading: verifyingOtp } = useMutation(
    verifyOTP_forConsent,
    {
      onSuccess: () => {
        toast.success("Plan accepted successfully!");
        setOtp("");
        setDialogOpen(false);
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Invalid OTP");
      },
    },
  );

  const handleSendOtp = () => {
    sendOtp({ session });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    verifyOtp({ otp: Number(otp), email: schoolInfo?.email, session });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setOtp("");
  };

  if (!session || !sessionInfo?._id) return null;

  return (
    <>
      {sessionInfo?.isVerified && (
        <Alert
          variant="outlined"
          sx={{
            borderRadius: 2,
            mb: 2,
            borderColor: "success.200",
            color: "success.dark",
            fontWeight: 500,
            fontSize: "1rem",
            backgroundColor: "#e8f5e9",
          }}
        >
          You have successfully accepted the subscription plan for this session
          by verifying the OTP
        </Alert>
      )}

      {!sessionInfo?.isVerified && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            borderRadius: 2,
            mb: 2,
            borderColor: "warning.main",
            color: "warning.main",
            fontWeight: 500,
            fontSize: "1rem",
            backgroundColor: "#fff3e0",
          }}
          action={
            <LoadingButton
              loading={sendingOtp}
              variant="outlined"
              color="warning"
              size="medium"
              onClick={handleSendOtp}
              disabled={!schoolInfo?.email}
              sx={{ whiteSpace: "nowrap", borderRadius: 50 }}
            >
              Send OTP
            </LoadingButton>
          }
        >
          To confirm your below subscriptions, please accept the plan by verifying your
          OTP. An OTP will be sent to the registered school email{" "}
          <strong>{schoolInfo?.email}</strong>.
        </Alert>
      )}

      <CommonDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
        title="Verify OTP"
      >
        <DialogContent>
          <Typography variant="body1" color="text.secondary" mb={1} mt={1}>
            An OTP has been sent to <strong>{schoolInfo?.email}</strong>. Enter
            the 6-digit code below to accept the plan.
          </Typography>
          <TextField
            value={otp}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              setOtp(val);
            }}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            size="small"
            fullWidth
            placeholder="6-digit OTP"
            autoFocus
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <LoadingButton
            loading={sendingOtp}
            variant="outlined"
            onClick={handleSendOtp}
            size="medium"
          >
            Resend OTP
          </LoadingButton>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={verifyingOtp}
            variant="contained"
            color="primary"
            onClick={handleVerifyOtp}
            size="medium"
          >
            Verify & Accept
          </LoadingButton>
        </DialogActions>
      </CommonDialog>
    </>
  );
};

export default OtpPlanAcceptance;
